import { LinearGradient } from 'expo-linear-gradient';
import type { ReactNode } from 'react';
import { Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { View } from '@/components/ui/view';
import { useColors } from '@/hooks/useColors';

/** Height of the fade that lets messages dissolve behind the floating composer. */
const COMPOSER_FADE_HEIGHT = 112;

/**
 * The space under the floating panel, above the tab bar or the keyboard. Its
 * shadow and its width past the tab bar's inset made the in-flow box's 4pt gap
 * read as the two touching.
 */
const FLOATING_BOTTOM_GAP = 8;

/**
 * Whether the native composer floats: wherever Liquid Glass is drawn, which is
 * never on web. The chat route, the chat and the dock all read it here so they
 * cannot disagree about who pads for the tab bar.
 */
export function useComposerFloats(): boolean {
  return useLiquidGlass() && Platform.OS !== 'web';
}

type NativeComposerDockProps = {
  children: ReactNode;
  /** Banners and the action row, floated above the composer. */
  overlay: ReactNode;
  /** Space between the screen's sides and the composer. */
  horizontalPadding: number;
  /**
   * Chrome the floating composer rides above, such as the tab bar. The fade runs
   * on under it in the same solid fill, so the bar sits on the composer's own
   * backdrop rather than on whatever message is passing beneath.
   */
  bottomInset?: number;
  /** Space under the in-flow composer, such as the gap above the keyboard. */
  inFlowBottomGap: number;
  isDisabled: boolean;
  /** The composer's height while it floats, which the list takes as scroll room. */
  onFloatingHeightChange: (height: number) => void;
};

/**
 * The native inquiry chat's composer shell.
 *
 * Where Liquid Glass is drawn it matches Research and the support thread: one
 * rounded glass panel floating over the messages, which scroll behind it under
 * a fade. A floating composer covers the end of the list, so it reports its
 * height and the chat pads the list by it. If that report is ever dropped,
 * the float has to go with it. Its empty edges let touches through to the
 * messages scrolling beneath, as the overlay above it does.
 *
 * Elsewhere it stays in flow and the toolbar keeps its own bordered box. Glass
 * has no gray fill to show a disabled composer, so it dims instead.
 */
export function NativeComposerDock({
  children,
  overlay,
  horizontalPadding,
  bottomInset = 0,
  inFlowBottomGap,
  isDisabled,
  onFloatingHeightChange,
}: NativeComposerDockProps) {
  const isFloating = useComposerFloats();

  const { colors } = useColors();

  if (!isFloating) {
    return (
      <View style={{ paddingHorizontal: horizontalPadding, paddingBottom: inFlowBottomGap }}>
        <View className="relative">
          {overlay}
          {children}
        </View>
      </View>
    );
  }

  return (
    <View
      pointerEvents="box-none"
      className="absolute bottom-0 left-0 right-0"
      onLayout={(event) => onFloatingHeightChange(Math.round(event.nativeEvent.layout.height))}>
      <LinearGradient
        colors={[`${colors.background[0]}00`, colors.background[0], colors.background[0]]}
        locations={[0, COMPOSER_FADE_HEIGHT / (COMPOSER_FADE_HEIGHT + bottomInset), 1]}
        pointerEvents="none"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: COMPOSER_FADE_HEIGHT + bottomInset,
        }}
      />

      <View
        pointerEvents="box-none"
        style={{
          paddingHorizontal: horizontalPadding,
          paddingBottom: bottomInset + FLOATING_BOTTOM_GAP,
        }}>
        <View className="relative">
          {overlay}
          <View className="rounded-[28px] shadow-soft-2">
            <GlassSurface className="w-full rounded-[28px]">
              <View className={twJoin('px-2 py-1', isDisabled && 'opacity-60')}>{children}</View>
            </GlassSurface>
          </View>
        </View>
      </View>
    </View>
  );
}
